// `trackEvent` est défini de façon canonique, et soumise au consentement, dans
// components/Analytics.ts. On le ré-exporte ici pour que les imports existants
// (`./utils/analytics`) continuent de fonctionner sans changement.
import { trackEvent } from '../components/Analytics';
export { trackEvent };

// Tracker pour le temps de lecture
export const initReadingTimeTracker = (articleId: string) => {
  let startTime = Date.now();
  let tracked25 = false;
  let tracked50 = false;
  let tracked75 = false;
  let tracked100 = false;

  let ticking = false; // Pour éviter des appels multiples à `scroll`

  const calculateReadingProgress = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY;
        const progress = (scrollTop / (documentHeight - windowHeight)) * 100;

        if (progress >= 25 && !tracked25) {
          trackEvent('engagement', 'reading_progress', articleId, 25);
          tracked25 = true;
        }
        if (progress >= 50 && !tracked50) {
          trackEvent('engagement', 'reading_progress', articleId, 50);
          tracked50 = true;
        }
        if (progress >= 75 && !tracked75) {
          trackEvent('engagement', 'reading_progress', articleId, 75);
          tracked75 = true;
        }
        if (progress >= 95 && !tracked100) {
          const timeSpent = Math.round((Date.now() - startTime) / 1000);
          trackEvent('engagement', 'article_completed', articleId, timeSpent);
          tracked100 = true;
        }

        ticking = false; // Permet un nouveau calcul au prochain scroll
      });

      ticking = true; // Empêche d'exécuter plusieurs animations en même temps
    }
  };

  window.addEventListener('scroll', calculateReadingProgress);
};


// Tracker pour les liens externes
export const trackExternalLinks = () => {
  document.querySelectorAll('a').forEach(link => {
    if (link.hostname !== window.location.hostname && link.href.startsWith('http')) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        trackEvent('outbound', 'link_click', link.href);
        setTimeout(() => {
          window.open(link.href, '_blank');
        }, 100);
      });
    }
  });
};

// Tracker pour les formulaires
export const trackFormSubmission = (formId: string) => {
  const form = document.getElementById(formId) as HTMLFormElement;
  if (form) {
    form.addEventListener('submit', (_e) => {
      trackEvent('conversion', 'form_submit', formId);
    });
  }
};

const inferCtaType = (element: HTMLElement): string => {
  const explicit = element.getAttribute('data-cta');
  if (explicit) {
    return explicit;
  }

  if (element instanceof HTMLAnchorElement) {
    const href = (element.getAttribute('href') || '').toLowerCase();

    if (href.includes('calendly')) return 'demo';
    if (href.includes('register')) return 'signup';
    if (href.includes('login')) return 'login';
    if (href.includes('contact')) return 'contact';
    if (href.includes('checklist-crypto')) return 'checklist';
    if (href.startsWith('mailto:')) return 'email';
  }

  return 'cta';
};

const resolveCtaLocation = (element: HTMLElement): string => {
  return (
    element.getAttribute('data-cta-location') ||
    element.closest<HTMLElement>('[data-section]')?.getAttribute('data-section') ||
    element.closest<HTMLElement>('section')?.id ||
    'unknown'
  );
};

// Mapping data-cta-location -> partenaire, pour les événements de conversion dédiés.
const PARTNER_BY_LOCATION: Record<string, string> = {
  'partner-pennylane': 'pennylane',
  'partner-tiime': 'tiime',
  'dougs': 'dougs',
};

export const trackCtaClicks = () => {
  const ctaElements = document.querySelectorAll<HTMLElement>('[data-cta], a.cta-button');

  ctaElements.forEach(element => {
    if (element.getAttribute('data-cta-bound') === 'true') {
      return;
    }

    element.setAttribute('data-cta-bound', 'true');
    element.addEventListener('click', () => {
      const ctaType = inferCtaType(element);
      const location = resolveCtaLocation(element);
      const href = element instanceof HTMLAnchorElement ? (element.getAttribute('href') || '') : '';
      const label = [location, href].filter(Boolean).join(' | ') || undefined;

      // Événement générique (granularité fine, conservé)
      trackEvent('cta', ctaType, label);

      // Événements de conversion dédiés (à marquer "événements clés" dans GA4)
      if (ctaType === 'signup') {
        const partner = PARTNER_BY_LOCATION[location];
        if (partner) {
          trackEvent('partner', 'partner_signup_click', label, undefined, { partner, cta_location: location });
        } else {
          trackEvent('conversion', 'signup_click', label, undefined, { cta_location: location });
        }
      } else if (ctaType === 'demo') {
        trackEvent('conversion', 'demo_click', label, undefined, { cta_location: location });
      }
    });
  });
};

export const trackChecklistDownload = (label?: string) => {
  const eventLabel = label || `Checklist download from ${window.location.pathname}`;

  trackEvent('conversion', 'checklist_download', eventLabel);

  if ((window as any).gtag) {
    (window as any).gtag('event', 'checklist_download', {
      event_category: 'conversion',
      event_label: eventLabel,
      value: 1
    });

    (window as any).gtag('event', 'generate_lead', {
      event_category: 'conversion',
      event_label: 'Checklist download',
      form_id: 'checklistForm',
      lead_type: 'checklist_crypto',
      value: 1
    });
  }
};
